import { useRef, useState, type ChangeEvent, type KeyboardEvent } from 'react';
import { RotateCcw } from 'lucide-react';
import { motion } from 'framer-motion';
import {
  TIERS,
  type Tier,
  formatGBPCompact,
  formatGBPRange,
  formatRange,
  tierTotals,
} from '@/lib/roi/data';

interface RoleDraft {
  role: string;
  rate: string;
  hours: string;
}

interface TierDraft {
  casesPerYear: string;
  roles: RoleDraft[];
}

const DEFAULT_TIER = TIERS[0]!;

function getTier(id: Tier['id']): Tier {
  return TIERS.find((tier) => tier.id === id) ?? DEFAULT_TIER;
}

function midpoint(range: Tier['casesPerYear']): number {
  return range.high === undefined ? range.low : (range.low + range.high) / 2;
}

function defaultDraft(tier: Tier): TierDraft {
  return {
    casesPerYear: String(midpoint(tier.casesPerYear)),
    roles: tier.rolePresets.map((preset) => ({
      role: preset.role,
      rate: String(preset.chargeOutRate),
      hours: String(preset.hoursPerCase),
    })),
  };
}

function initialDrafts(): Record<Tier['id'], TierDraft> {
  return Object.fromEntries(
    TIERS.map((tier) => [tier.id, defaultDraft(tier)]),
  ) as Record<Tier['id'], TierDraft>;
}

/** Keep an editable decimal draft while dropping formatting and negatives. */
function sanitiseDraft(value: string): string {
  const numericCharacters = value.replace(/[^\d.]/g, '');
  const [integer = '', ...decimalParts] = numericCharacters.split('.');
  return decimalParts.length > 0
    ? `${integer}.${decimalParts.join('')}`
    : integer;
}

/**
 * Clearable text-number drafts: a blank or unparseable field stays visually
 * blank and counts as zero in live calculations. Pasted currency separators
 * (£, commas, spaces) are ignored.
 */
function draftToNumber(value: string): number {
  const normalised = value.replace(/[£,\s]/g, '');
  if (normalised.length === 0) return 0;
  const parsed = Number(normalised);
  return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
}

function formatBaseline(tier: Tier): string {
  const totals = tierTotals(tier);
  return tier.casesPerYear.high === undefined
    ? formatGBPCompact(totals.savingLow)
    : formatGBPRange(totals.savingLow, totals.savingHigh);
}

export function SelfServiceCalculator() {
  const [selectedTierId, setSelectedTierId] = useState<Tier['id']>('large');
  const selectedTier = getTier(selectedTierId);
  const [drafts, setDrafts] =
    useState<Record<Tier['id'], TierDraft>>(initialDrafts);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const draft = drafts[selectedTier.id];
  const savingPercent = selectedTier.timeSavingPercent;
  const roleCosts = draft.roles.map(
    (role) => draftToNumber(role.rate) * draftToNumber(role.hours),
  );
  const blendedWipPerCase = roleCosts.reduce((sum, cost) => sum + cost, 0);
  const casesPerYear = draftToNumber(draft.casesPerYear);
  const annualWip = casesPerYear * blendedWipPerCase;
  const annualSaving = annualWip * (savingPercent / 100);
  const monthlySaving = annualSaving / 12;
  const baselineSaving = formatBaseline(selectedTier);

  const updateDraft = (updater: (current: TierDraft) => TierDraft) => {
    setDrafts((current) => ({
      ...current,
      [selectedTier.id]: updater(current[selectedTier.id]),
    }));
  };

  const handleCasesChange = (event: ChangeEvent<HTMLInputElement>) => {
    const next = sanitiseDraft(event.target.value);
    updateDraft((current) => ({ ...current, casesPerYear: next }));
  };

  const handleRoleChange =
    (index: number, key: 'rate' | 'hours') =>
    (event: ChangeEvent<HTMLInputElement>) => {
      const next = sanitiseDraft(event.target.value);
      updateDraft((current) => ({
        ...current,
        roles: current.roles.map((role, i) =>
          i === index ? { ...role, [key]: next } : role,
        ),
      }));
    };

  /** Reset restores only the active firm-size tab; other tabs keep edits. */
  const resetToPreset = () => updateDraft(() => defaultDraft(selectedTier));

  const handleTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const currentIndex = TIERS.findIndex((tier) => tier.id === selectedTierId);
    let nextIndex: number | null = null;
    if (event.key === 'ArrowRight') {
      nextIndex = (currentIndex + 1) % TIERS.length;
    } else if (event.key === 'ArrowLeft') {
      nextIndex = (currentIndex - 1 + TIERS.length) % TIERS.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = TIERS.length - 1;
    }
    if (nextIndex === null) return;
    event.preventDefault();
    const nextTier = TIERS[nextIndex]!;
    setSelectedTierId(nextTier.id);
    tabRefs.current[nextIndex]?.focus();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
      className="grid gap-6 lg:grid-cols-[minmax(0,1.08fr)_minmax(360px,0.92fr)]"
    >
      <div
        role="group"
        aria-label="Standard ROI calculator controls"
        className="pitch-card flex flex-col gap-6 rounded-xl p-6"
      >
        <div className="flex flex-col gap-3">
          <span className="text-[13px] font-medium text-ink-muted">
            Start from a validated profile
          </span>
          <div
            role="tablist"
            aria-label="Firm size"
            className="grid gap-3 md:grid-cols-3"
          >
            {TIERS.map((tier, index) => {
              const selected = tier.id === selectedTier.id;
              return (
                <button
                  key={tier.id}
                  ref={(el) => {
                    tabRefs.current[index] = el;
                  }}
                  type="button"
                  role="tab"
                  id={`roi-firm-size-tab-${tier.id}`}
                  aria-selected={selected}
                  aria-controls="roi-firm-size-panel"
                  tabIndex={selected ? 0 : -1}
                  onClick={() => setSelectedTierId(tier.id)}
                  onKeyDown={handleTabKeyDown}
                  className={
                    'flex min-h-[112px] flex-col items-start justify-between gap-3 rounded-lg border p-4 text-left transition focus:outline-none focus:ring-2 focus:ring-brand-500/40 ' +
                    (selected
                      ? 'border-brand-500 bg-accent-blue-bg text-ink-strong'
                      : 'border-border bg-transparent text-ink-muted hover:border-border-strong hover:bg-bg-tile/40')
                  }
                >
                  <span className="text-sm font-semibold">{tier.label}</span>
                  <span className="text-xs leading-snug text-ink-muted">
                    {tier.profile}
                  </span>
                  <span className="text-sm font-semibold tabular-nums text-brand-500">
                    {formatBaseline(tier)} baseline
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div
          role="tabpanel"
          id="roi-firm-size-panel"
          aria-labelledby={`roi-firm-size-tab-${selectedTier.id}`}
          className="border-t border-border-subtle pt-6"
        >
          <div className="flex flex-col gap-6">
            <div className="flex max-w-xs flex-col gap-3">
              <label
                htmlFor="roi-cases-per-year"
                className="text-[13px] font-medium text-ink-muted"
              >
                Cases per year
              </label>
              <DraftInput
                id="roi-cases-per-year"
                value={draft.casesPerYear}
                onChange={handleCasesChange}
              />
            </div>

            <div className="flex flex-col gap-3">
              <span className="text-[13px] font-medium text-ink-muted">
                Role cost blend per case
              </span>
              <p className="text-xs leading-relaxed text-ink-dim">
                Editable starting mix calibrated to the current{' '}
                {formatGBPCompact(selectedTier.spendPerCase)} per-case baseline
              </p>
              <div
                aria-hidden
                className="hidden gap-3 text-xs text-ink-dim sm:grid sm:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(72px,auto)]"
              >
                <span>Role</span>
                <span>Charge-out rate</span>
                <span>Avg hours per case</span>
                <span className="text-right">Cost per case</span>
              </div>
              {draft.roles.map((role, index) => (
                <div
                  key={role.role}
                  className="grid gap-3 sm:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(72px,auto)] sm:items-center"
                >
                  <span className="text-sm font-medium text-ink-strong">
                    {role.role}
                  </span>
                  <DraftInput
                    label={`${role.role} charge-out rate`}
                    prefix="£"
                    suffix="/hr"
                    value={role.rate}
                    onChange={handleRoleChange(index, 'rate')}
                  />
                  <DraftInput
                    label={`${role.role} average hours per case`}
                    suffix="hrs"
                    value={role.hours}
                    onChange={handleRoleChange(index, 'hours')}
                  />
                  <span className="text-sm tabular-nums text-ink-muted sm:text-right">
                    {formatGBPCompact(roleCosts[index]!)} per case
                  </span>
                </div>
              ))}
              <div className="flex items-baseline justify-between gap-3 border-t border-border-subtle pt-3">
                <span className="text-[13px] font-medium text-ink-muted">
                  Blended WIP per case
                </span>
                <span className="text-sm font-semibold tabular-nums text-ink-strong">
                  {formatGBPCompact(blendedWipPerCase)}
                </span>
              </div>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-baseline gap-x-2 gap-y-1 rounded-md bg-bg-tile/20 px-3 py-2 text-xs leading-relaxed text-ink-muted">
            <span className="font-medium text-ink-strong">
              Asset Hound saving assumption:
            </span>
            <span>
              Saves {savingPercent}% of manual time, locked from practitioner
              interviews and not entered by the viewer
            </span>
          </div>
        </div>
      </div>

      <aside
        aria-label="Standard ROI calculator result"
        className="pitch-card flex flex-col justify-between gap-8 rounded-xl p-6"
      >
        <div className="flex flex-col gap-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex flex-col gap-1">
              <span className="text-[13px] font-medium text-ink-muted">
                Current annual saving
              </span>
              <span className="text-[48px] font-semibold leading-none text-brand-500 sm:text-[56px]">
                {formatGBPCompact(annualSaving)}
              </span>
            </div>
            <button
              type="button"
              onClick={resetToPreset}
              className="inline-flex h-9 items-center gap-2 rounded-md border border-accent-blue-border px-3 text-xs font-medium text-brand-500 transition hover:border-brand-500 hover:bg-accent-blue-bg focus:outline-none focus:ring-2 focus:ring-brand-500/40 active:scale-[0.98]"
            >
              <RotateCcw aria-hidden className="h-3.5 w-3.5" />
              Reset
            </button>
          </div>

          <p className="max-w-[52ch] text-sm leading-relaxed text-ink-muted">
            This uses the selected profile as the evidence-backed starting
            point, then recalculates from case volume, charge-out rates, hours
            per case, and the locked Asset Hound saving assumption
          </p>
        </div>

        <dl className="grid grid-cols-2 gap-x-5 gap-y-4 border-t border-border-subtle pt-6 text-sm">
          <Metric label="Annual WIP" value={formatGBPCompact(annualWip)} />
          <Metric
            label="Blended WIP per case"
            value={formatGBPCompact(blendedWipPerCase)}
          />
          <Metric
            label="Monthly saving"
            value={formatGBPCompact(monthlySaving)}
          />
          <Metric
            label="Asset Hound saving"
            value={`${savingPercent}% of time`}
          />
          <Metric
            label="Goes to plan"
            value={formatRange(selectedTier.goingToPlanPercent, '%')}
          />
          <Metric
            label="Validated baseline"
            value={baselineSaving}
            wide={selectedTier.casesPerYear.high !== undefined}
          />
        </dl>
      </aside>
    </motion.div>
  );
}

function DraftInput({
  id,
  label,
  value,
  onChange,
  prefix,
  suffix,
}: {
  id?: string;
  label?: string;
  value: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  prefix?: string;
  suffix?: string;
}) {
  return (
    <div className="flex items-center rounded-md border border-border-strong bg-bg-input px-3 focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-500/30">
      {prefix ? <span className="text-sm text-ink-muted">{prefix}</span> : null}
      <input
        id={id}
        aria-label={label}
        type="text"
        inputMode="decimal"
        value={value}
        onChange={onChange}
        className="h-11 min-w-0 flex-1 bg-transparent px-2 text-sm font-medium tabular-nums text-ink-strong outline-none"
      />
      {suffix ? <span className="text-sm text-ink-muted">{suffix}</span> : null}
    </div>
  );
}

function Metric({
  label,
  value,
  wide = false,
}: {
  label: string;
  value: string;
  wide?: boolean;
}) {
  return (
    <div className={'flex flex-col gap-1 ' + (wide ? 'col-span-2' : '')}>
      <dt className="text-xs text-ink-dim">{label}</dt>
      <dd className="text-sm font-semibold tabular-nums text-ink-strong">
        {value}
      </dd>
    </div>
  );
}
